import { z } from 'zod';
import { AutomationLevel, RecommendationSeverity } from '../enums';

/** Situações detectadas pela etapa DETERMINÍSTICA (sem IA). */
export const RuleCode = {
  CPL_ABOVE_TARGET: 'CPL_ABOVE_TARGET',
  LOW_CTR: 'LOW_CTR',
  CREATIVE_FATIGUE: 'CREATIVE_FATIGUE',
  BUDGET_EXHAUSTED_EARLY: 'BUDGET_EXHAUSTED_EARLY',
  BUDGET_UNDERSPENT: 'BUDGET_UNDERSPENT',
  IRRELEVANT_SEARCH_TERM: 'IRRELEVANT_SEARCH_TERM',
  AD_DISAPPROVED: 'AD_DISAPPROVED',
  CONVERSION_DROP: 'CONVERSION_DROP',
  NO_CONVERSIONS: 'NO_CONVERSIONS',
  LEARNING_LIMITED: 'LEARNING_LIMITED',
  HIGH_FREQUENCY: 'HIGH_FREQUENCY',
  ACCOUNT_DISCONNECTED: 'ACCOUNT_DISCONNECTED',
  LOW_QUALITY_LEADS: 'LOW_QUALITY_LEADS',
  COST_PER_INSTALL_ABOVE_TARGET: 'COST_PER_INSTALL_ABOVE_TARGET',
  GEO_WITHOUT_COVERAGE: 'GEO_WITHOUT_COVERAGE',
} as const;
export type RuleCode = (typeof RuleCode)[keyof typeof RuleCode];

export const RULE_LABELS: Record<RuleCode, string> = {
  CPL_ABOVE_TARGET: 'CPL acima da meta',
  LOW_CTR: 'CTR abaixo do esperado',
  CREATIVE_FATIGUE: 'Fadiga de criativo',
  BUDGET_EXHAUSTED_EARLY: 'Orçamento esgotando cedo',
  BUDGET_UNDERSPENT: 'Orçamento subutilizado',
  IRRELEVANT_SEARCH_TERM: 'Termo de busca irrelevante',
  AD_DISAPPROVED: 'Anúncio reprovado',
  CONVERSION_DROP: 'Queda de conversão',
  NO_CONVERSIONS: 'Sem conversões no período',
  LEARNING_LIMITED: 'Conjunto em aprendizado limitado',
  HIGH_FREQUENCY: 'Frequência alta',
  ACCOUNT_DISCONNECTED: 'Conta desconectada',
  LOW_QUALITY_LEADS: 'Leads de baixa qualidade',
  COST_PER_INSTALL_ABOVE_TARGET: 'Custo por instalação acima da meta',
  GEO_WITHOUT_COVERAGE: 'Veiculação em área sem cobertura',
};

/** Evidência numérica produzida pela regra — a IA nunca inventa números. */
export const ruleFindingSchema = z.object({
  code: z.nativeEnum(RuleCode),
  severity: z.nativeEnum(RecommendationSeverity),
  entityLevel: z.enum(['ACCOUNT', 'CAMPAIGN', 'AD_SET', 'AD', 'KEYWORD']),
  entityId: z.string(),
  entityName: z.string(),
  metrics: z.record(z.string(), z.number()),
  observedValue: z.number(),
  thresholdValue: z.number(),
  windowDays: z.number().int(),
  message: z.string().max(500),
});
export type RuleFinding = z.infer<typeof ruleFindingSchema>;

/** Saída da IA na etapa interpretativa. */
export const recommendationGenerationSchema = z.object({
  recommendations: z
    .array(
      z.object({
        findingCode: z.nativeEnum(RuleCode),
        entityId: z.string().max(120),
        problem: z.string().max(600),
        evidence: z.string().max(800),
        suggestion: z.string().max(1000),
        risk: z.string().max(600),
        recommendedAction: z.object({
          type: z.enum([
            'PAUSE_ENTITY',
            'ADJUST_BUDGET',
            'ADJUST_BID',
            'REPLACE_CREATIVE',
            'ADD_NEGATIVE_KEYWORD',
            'NARROW_GEO',
            'EXPAND_AUDIENCE',
            'REWRITE_COPY',
            'FIX_POLICY_ISSUE',
            'RECONNECT_ACCOUNT',
            'NO_ACTION',
          ]),
          parameters: z.string().max(600),
          expectedImpact: z.string().max(400),
        }),
        confidence: z.enum(['LOW', 'MEDIUM', 'HIGH']),
        priority: z.number().int(),
      }),
    )
    .max(30),
  summary: z.string().max(2000),
});
export type RecommendationGeneration = z.infer<typeof recommendationGenerationSchema>;

/** Ações que PODEM ser automatizadas quando o nível é CONTROLLED_AUTOMATION. */
export const PRE_AUTHORIZED_ACTIONS = [
  'PAUSE_ENTITY',
  'ADD_NEGATIVE_KEYWORD',
  'FIX_POLICY_ISSUE',
] as const;

export const optimizationSettingsSchema = z.object({
  automationLevel: z.nativeEnum(AutomationLevel).default('RECOMMEND_ONLY'),
  cplTolerancePct: z.number().min(0).max(300).default(20),
  minCtrPct: z.number().min(0).max(20).default(0.8),
  frequencyThreshold: z.number().min(1).max(20).default(3.5),
  creativeFatigueDays: z.number().int().min(3).max(90).default(14),
  conversionDropPct: z.number().min(5).max(100).default(30),
  minSpendForAnalysisCents: z.number().int().min(0).default(5000),
  windowDays: z.number().int().min(3).max(90).default(7),
  /** Ações automáticas só liberam após histórico mínimo. */
  minHistoryDaysForAutomation: z.number().int().min(30).default(60),
});
export type OptimizationSettingsInput = z.infer<typeof optimizationSettingsSchema>;

export const applyRecommendationSchema = z.object({
  note: z.string().max(1000).optional(),
  /** Confirmação explícita para ações que mexem em verba. */
  confirmBudgetImpact: z.boolean().default(false),
});
export type ApplyRecommendationInput = z.infer<typeof applyRecommendationSchema>;
